import React, { useState, useEffect } from 'react';
import { sanitizeInput, isValidEmail, isValidYear, isValidFullName, isValidLeadStatus, getCurrentYear, getMaxYear } from '../lib/validation';

// Extended data interface for WordPress widget
export interface ExtendedFormData {
  fullName: string;
  email: string;
  yearOfTravel: string;
  status: 'Researching' | 'Ready to Book';
  destination?: string; // Single-select
  travelCompanions?: 'Family' | 'Solo' | 'Couple' | 'Friends';
  budget?: string;
  safariNights?: string;
}

export interface TourOperatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    fullName: string;
    email: string;
    yearOfTravel: string;
    status: 'Researching' | 'Ready to Book';
  } | ExtendedFormData) => void;
  isLoading?: boolean;
  shouldReset?: boolean;
  extendedMode?: boolean; // Enable multi-step form for WordPress widget
}

export function TourOperatorModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  shouldReset,
  extendedMode = false
}: TourOperatorModalProps) {
  // Basic form fields (Step 1)
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [yearOfTravel, setYearOfTravel] = useState('');
  const [status, setStatus] = useState<'Researching' | 'Ready to Book' | ''>('');

  // Extended form fields (Step 2)
  const [destination, setDestination] = useState('');
  const [travelCompanions, setTravelCompanions] = useState('');
  const [budget, setBudget] = useState('');
  const [safariNights, setSafariNights] = useState('');

  // Multi-step form state
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = extendedMode ? 2 : 1;
  const [justNavigated, setJustNavigated] = useState(false);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Destination options
  const destinationOptions = [
    'South Africa', 'Kenya', 'Tanzania', 'Namibia',
    'Zambia', 'Botswana', 'Zimbabwe', 'Uganda', 'Rwanda'
  ];

  // Budget options (without descriptions for cleaner UI)
  const budgetOptions = [
    { value: 'Value', label: 'Value ($200-400/night)' },
    { value: 'Mid-Range', label: 'Mid-Range ($400-800/night)' },
    { value: 'Upper Mid', label: 'Upper Mid ($800-1,200/night)' },
    { value: 'Luxury', label: 'Luxury ($1,200-2,000/night)' },
    { value: 'Ultra-Luxury', label: 'Ultra-Luxury ($2,000+/night)' }
  ];

  // Safari nights options
  const nightsOptions = [
    '3 to 5 nights',
    '6 to 8 nights',
    '9 to 12 nights',
    '13+ nights'
  ];

  // Reset form when shouldReset prop changes to true
  useEffect(() => {
    if (shouldReset) {
      // Reset basic fields
      setFullName('');
      setEmail('');
      setYearOfTravel('');
      setStatus('');
      // Reset extended fields
      setDestination('');
      setTravelCompanions('');
      setBudget('');
      setSafariNights('');
      // Reset state
      setErrors({});
      setCurrentStep(1);
      setJustNavigated(false);
    }
  }, [shouldReset]);

  // Reset step when modal closes
  useEffect(() => {
    console.log('Modal isOpen changed to:', isOpen);
    if (!isOpen) {
      setCurrentStep(1);
      setErrors({});
      setJustNavigated(false);
    } else {
      // When modal opens, ensure clean state
      console.log('Modal opened, ensuring clean state');
      setErrors({});
      setJustNavigated(false);
    }
  }, [isOpen]);

  const validateStep1 = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    // Validate full name
    if (!fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (!isValidFullName(fullName)) {
      newErrors.fullName = 'Please enter a valid name (letters only)';
    }

    // Validate email
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmail(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Validate year of travel
    if (!yearOfTravel) {
      newErrors.yearOfTravel = 'Year of travel is required';
    } else if (!isValidYear(yearOfTravel)) {
      newErrors.yearOfTravel = `Please enter a valid year (${getCurrentYear()}-${getMaxYear()})`;
    }

    // Validate status
    if (!status) {
      newErrors.status = 'Please select your planning status';
    } else if (!isValidLeadStatus(status)) {
      newErrors.status = 'Please select a valid status';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!destination) {
      newErrors.destination = 'Please select a destination';
    }

    if (!travelCompanions) {
      newErrors.travelCompanions = 'Please select who you are travelling with';
    }

    if (!budget) {
      newErrors.budget = 'Please select your budget range';
    }

    if (!safariNights) {
      newErrors.safariNights = 'Please select the number of nights';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleNextStep = () => {
    let isValid = false;

    if (currentStep === 1) {
      isValid = validateStep1();
    } else if (currentStep === 2) {
      isValid = validateStep2();
    }

    if (isValid) {
      console.log('Moving from step', currentStep, 'to step', currentStep + 1);

      // Set navigation flag to prevent immediate submit processing
      setJustNavigated(true);
      setTimeout(() => {
        setJustNavigated(false);
        console.log('Navigation flag cleared');
      }, 100);

      setCurrentStep(currentStep + 1);
      // Clear ALL errors when moving to next step
      setErrors({});
    }
  };

  const handlePrevStep = () => {
    setCurrentStep(currentStep - 1);
    setErrors({});
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('handleSubmit called, currentStep:', currentStep, 'justNavigated:', justNavigated);
    console.trace('Submit call stack');

    // Ignore submits that happen immediately after navigation
    if (justNavigated) {
      console.log('Ignoring submit because we just navigated');
      return;
    }

    if (!extendedMode) {
      // Original behavior for main app
      if (!validateStep1()) return;

      onSubmit({
        fullName: sanitizeInput(fullName),
        email: sanitizeInput(email),
        yearOfTravel: sanitizeInput(yearOfTravel),
        status: status as 'Researching' | 'Ready to Book'
      });
    } else {
      // Extended behavior for WordPress widget
      // IMPORTANT: Only process submit on step 2 (the final step), ignore on other steps
      if (currentStep !== 2) {
        console.log('Not on step 2 (currentStep:', currentStep, '), ignoring submit');
        return;
      }

      onSubmit({
        fullName: sanitizeInput(fullName),
        email: sanitizeInput(email),
        yearOfTravel: sanitizeInput(yearOfTravel),
        status: status as 'Researching' | 'Ready to Book',
        destination,
        travelCompanions: travelCompanions as 'Family' | 'Solo' | 'Couple' | 'Friends',
        budget,
        safariNights
      });
    }
  };

  if (!isOpen) return null;

  // ORIGINAL STYLING FOR MAIN APP
  if (!extendedMode) {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 md:p-4"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50,
          padding: '16px'
        }}
      >
        <div
          className="bg-mobile-background w-full h-full md:h-auto md:rounded-2xl p-6 md:p-8 md:max-w-lg md:mx-4 shadow-2xl relative overflow-y-auto"
          style={{
            backgroundColor: '#F6F7E9',
            width: '100%',
            maxWidth: '512px',
            height: 'auto',
            maxHeight: '95vh',
            borderRadius: '16px',
            padding: '32px',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)',
            position: 'relative',
            overflowY: 'auto',
            textAlign: 'left'
          }}>
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 text-4xl leading-none font-thin bg-mobile-background shadow-none z-10"
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              fontSize: '36px',
              lineHeight: 1,
              fontWeight: 100,
              color: '#6B7280',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px 8px',
              zIndex: 10
            }}
            aria-label="Close"
          >
            ×
          </button>

          <h2
            className="text-[#3A4A52] text-2xl font-semibold mb-8 mt-8 md:mt-0"
            style={{
              color: '#3A4A52',
              fontSize: '24px',
              fontWeight: 600,
              marginBottom: '32px',
              marginTop: '32px'
            }}
          >
            Plan Your Safari Adventure
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6 pb-6" style={{ paddingBottom: '24px', textAlign: 'left' }}>
            {/* Full Name */}
            <div style={{ marginBottom: '24px' }}>
              <label
                className="block text-[#3A4A52] text-sm font-medium mb-3"
                style={{
                  display: 'block',
                  color: '#3A4A52',
                  fontSize: '14px',
                  fontWeight: 500,
                  marginBottom: '12px',
                  textAlign: 'left'
                }}
              >
                Full Name *
              </label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => {
                  setFullName(e.target.value);
                  if (errors.fullName) setErrors({ ...errors, fullName: '' });
                }}
                placeholder="Enter your full name"
                className={`w-full px-4 py-3 bg-white rounded-xl border-none text-[#6B7280] placeholder-[#9CA3AF] focus:outline-none focus:ring-2 ${
                  errors.fullName ? 'ring-2 ring-red-500' : 'focus:ring-[#4A5568]'
                }`}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  border: errors.fullName ? '2px solid #EF4444' : 'none',
                  color: '#6B7280',
                  fontSize: '16px',
                  outline: 'none'
                }}
                disabled={isLoading}
              />
              {errors.fullName && (
                <p className="text-red-500 text-sm mt-2" style={{ color: '#EF4444', fontSize: '14px', marginTop: '8px' }}>
                  {errors.fullName}
                </p>
              )}
            </div>

            {/* Email */}
            <div style={{ marginBottom: '24px' }}>
              <label
                className="block text-[#3A4A52] text-sm font-medium mb-3"
                style={{
                  display: 'block',
                  color: '#3A4A52',
                  fontSize: '14px',
                  fontWeight: 500,
                  marginBottom: '12px',
                  textAlign: 'left'
                }}
              >
                Email Address *
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) setErrors({ ...errors, email: '' });
                }}
                placeholder="your@email.com"
                className={`w-full px-4 py-3 bg-white rounded-xl border-none text-[#6B7280] placeholder-[#9CA3AF] focus:outline-none focus:ring-2 ${
                  errors.email ? 'ring-2 ring-red-500' : 'focus:ring-[#4A5568]'
                }`}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  border: errors.email ? '2px solid #EF4444' : 'none',
                  color: '#6B7280',
                  fontSize: '16px',
                  outline: 'none'
                }}
                disabled={isLoading}
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-2" style={{ color: '#EF4444', fontSize: '14px', marginTop: '8px' }}>
                  {errors.email}
                </p>
              )}
            </div>

            {/* Year of Travel */}
            <div style={{ marginBottom: '24px' }}>
              <label
                className="block text-[#3A4A52] text-sm font-medium mb-3"
                style={{
                  display: 'block',
                  color: '#3A4A52',
                  fontSize: '14px',
                  fontWeight: 500,
                  marginBottom: '12px',
                  textAlign: 'left'
                }}
              >
                Year of Travel *
              </label>
              <select
                value={yearOfTravel}
                onChange={(e) => {
                  setYearOfTravel(e.target.value);
                  if (errors.yearOfTravel) setErrors({ ...errors, yearOfTravel: '' });
                }}
                className={`w-full px-4 py-3 bg-white rounded-xl border-none text-[#6B7280] focus:outline-none focus:ring-2 ${
                  errors.yearOfTravel ? 'ring-2 ring-red-500' : 'focus:ring-[#4A5568]'
                }`}
                style={{
                  width: '100%',
                  padding: '12px 40px 12px 16px',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  border: errors.yearOfTravel ? '2px solid #EF4444' : 'none',
                  color: '#6B7280',
                  fontSize: '16px',
                  outline: 'none',
                  cursor: 'pointer'
                }}
                disabled={isLoading}
              >
                <option value="">Select year</option>
                {[...Array(11)].map((_, i) => {
                  const year = getCurrentYear() + i;
                  return (
                    <option key={year} value={year}>{year}</option>
                  );
                })}
              </select>
              {errors.yearOfTravel && (
                <p className="text-red-500 text-sm mt-2" style={{ color: '#EF4444', fontSize: '14px', marginTop: '8px' }}>
                  {errors.yearOfTravel}
                </p>
              )}
            </div>

            {/* Lead Status - RADIO BUTTONS */}
            <div style={{ marginBottom: '24px' }}>
              <label
                className="block text-[#3A4A52] text-sm font-medium mb-3"
                style={{
                  display: 'block',
                  color: '#3A4A52',
                  fontSize: '14px',
                  fontWeight: 500,
                  marginBottom: '12px',
                  textAlign: 'left'
                }}
              >
                Where are you in your planning? *
              </label>
              <div className="space-y-3">
                <label
                  className="flex items-center cursor-pointer group"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    marginBottom: '12px'
                  }}
                >
                  <input
                    type="radio"
                    name="status"
                    value="Researching"
                    checked={status === 'Researching'}
                    onChange={(e) => {
                      setStatus(e.target.value as 'Researching');
                      if (errors.status) setErrors({ ...errors, status: '' });
                    }}
                    className="w-5 h-5 border-2 border-[#9CA3AF] text-[#22475d] focus:ring-0 focus:outline-none cursor-pointer accent-[#22475d]"
                    style={{
                      width: '20px',
                      height: '20px',
                      marginRight: '12px',
                      cursor: 'pointer',
                      accentColor: '#22475d'
                    }}
                    disabled={isLoading}
                    required
                  />
                  <span
                    className="text-[#6B7280] group-hover:text-[#3A4A52] transition-colors"
                    style={{
                      color: '#6B7280',
                      fontSize: '16px',
                      transition: 'color 0.2s'
                    }}
                  >
                    Still Researching
                  </span>
                </label>

                <label
                  className="flex items-center cursor-pointer group"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    marginBottom: '12px'
                  }}
                >
                  <input
                    type="radio"
                    name="status"
                    value="Ready to Book"
                    checked={status === 'Ready to Book'}
                    onChange={(e) => {
                      setStatus(e.target.value as 'Ready to Book');
                      if (errors.status) setErrors({ ...errors, status: '' });
                    }}
                    className="w-5 h-5 border-2 border-[#9CA3AF] text-[#22475d] focus:ring-0 focus:outline-none cursor-pointer accent-[#22475d]"
                    style={{
                      width: '20px',
                      height: '20px',
                      marginRight: '12px',
                      cursor: 'pointer',
                      accentColor: '#22475d'
                    }}
                    disabled={isLoading}
                    required
                  />
                  <span
                    className="text-[#6B7280] group-hover:text-[#3A4A52] transition-colors"
                    style={{
                      color: '#6B7280',
                      fontSize: '16px',
                      transition: 'color 0.2s'
                    }}
                  >
                    Ready to Book
                  </span>
                </label>
              </div>
              {errors.status && (
                <p className="text-red-500 text-sm mt-2" style={{ color: '#EF4444', fontSize: '14px', marginTop: '8px' }}>
                  {errors.status}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <div className="pt-4" style={{ paddingTop: '16px' }}>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#22475d] text-white py-3 px-6 rounded-xl font-semibold hover:bg-[#2d5a76] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  width: '100%',
                  backgroundColor: '#22475d',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: 600,
                  border: 'none',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  opacity: isLoading ? 0.5 : 1,
                  transition: 'background-color 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {isLoading ? (
                  <>
                    <svg
                      style={{
                        animation: 'spin 1s linear infinite',
                        marginRight: '8px',
                        width: '20px',
                        height: '20px'
                      }}
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        style={{ opacity: 0.25 }}
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        style={{ opacity: 0.75 }}
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Submitting...
                  </>
                ) : (
                  'Get Tour Operator Match'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // NEW EXTENDED MODE FOR WORDPRESS WIDGET - MATCHING MAIN APP STYLES
  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: `
        @media (min-width: 768px) {
          .widget-action-buttons {
            flex-direction: row !important;
          }
          .widget-action-buttons button:first-child {
            width: auto !important;
            flex: 0 0 auto !important;
          }
          .widget-action-buttons button:last-child {
            flex: 1 !important;
          }
        }
      `}} />
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 md:p-4"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50,
          padding: '16px'
        }}
      >
      <div
        className="bg-mobile-background w-full h-full md:h-auto md:rounded-2xl p-6 md:p-8 md:max-w-lg md:mx-4 shadow-2xl relative overflow-y-auto"
        style={{
          backgroundColor: '#F6F7E9',
          width: '100%',
          maxWidth: '512px',
          height: 'auto',
          maxHeight: '95vh',
          borderRadius: '16px',
          padding: extendedMode && currentStep === 2 ? '24px' : '32px',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)',
          position: 'relative',
          overflowY: 'auto',
          textAlign: 'left'
        }}>
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 text-4xl leading-none font-thin bg-mobile-background shadow-none z-10"
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            fontSize: '36px',
            lineHeight: 1,
            fontWeight: 100,
            color: '#6B7280',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '4px 8px',
            zIndex: 10
          }}
          aria-label="Close"
        >
          ×
        </button>

        <h2
          className="text-[#3A4A52] text-2xl font-semibold mb-8 mt-8 md:mt-0"
          style={{
            color: '#3A4A52',
            fontSize: '24px',
            fontWeight: 600,
            marginBottom: currentStep > 1 ? '16px' : '32px',
            marginTop: '32px'
          }}
        >
          Plan Your Safari Adventure
        </h2>

        {/* Step indicator - only show from step 2 onwards, matching cost-calculator style */}
        {currentStep > 1 && (
          <p style={{
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
            color: '#22485F',
            marginBottom: '24px',
            marginTop: '-8px'
          }}>
            Step {currentStep} of 2
          </p>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6 pb-6" style={{ paddingBottom: '24px', textAlign: 'left' }}>
          {/* Step 1: Basic Information */}
          {currentStep === 1 && (
            <>
              {/* Full Name */}
              <div style={{ marginBottom: '24px' }}>
                <label
                  className="block text-[#3A4A52] text-sm font-medium mb-3"
                  style={{
                    display: 'block',
                    color: '#3A4A52',
                    fontSize: '14px',
                    fontWeight: 500,
                    marginBottom: '12px',
                    textAlign: 'left'
                  }}
                >
                  Full Name *
                </label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => {
                    setFullName(e.target.value);
                    if (errors.fullName) setErrors({ ...errors, fullName: '' });
                  }}
                  placeholder="Enter your full name"
                  className={`w-full bg-white rounded-xl border-none text-[#6B7280] placeholder-[#9CA3AF] focus:outline-none focus:ring-2 ${
                    errors.fullName ? 'ring-2 ring-red-500' : 'focus:ring-[#4A5568]'
                  }`}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    border: errors.fullName ? '2px solid #EF4444' : 'none',
                    color: '#6B7280',
                    fontSize: '16px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  disabled={isLoading}
                />
                {errors.fullName && (
                  <p className="text-red-500 text-sm mt-2" style={{ color: '#EF4444', fontSize: '14px', marginTop: '8px' }}>
                    {errors.fullName}
                  </p>
                )}
              </div>

              {/* Email */}
              <div style={{ marginBottom: '24px' }}>
                <label
                  className="block text-[#3A4A52] text-sm font-medium mb-3"
                  style={{
                    display: 'block',
                    color: '#3A4A52',
                    fontSize: '14px',
                    fontWeight: 500,
                    marginBottom: '12px',
                    textAlign: 'left'
                  }}
                >
                  Email Address *
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) setErrors({ ...errors, email: '' });
                  }}
                  placeholder="your@email.com"
                  className={`w-full bg-white rounded-xl border-none text-[#6B7280] placeholder-[#9CA3AF] focus:outline-none focus:ring-2 ${
                    errors.email ? 'ring-2 ring-red-500' : 'focus:ring-[#4A5568]'
                  }`}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    border: errors.email ? '2px solid #EF4444' : 'none',
                    color: '#6B7280',
                    fontSize: '16px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  disabled={isLoading}
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-2" style={{ color: '#EF4444', fontSize: '14px', marginTop: '8px' }}>
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Year of Travel */}
              <div style={{ marginBottom: '24px' }}>
                <label
                  className="block text-[#3A4A52] text-sm font-medium mb-3"
                  style={{
                    display: 'block',
                    color: '#3A4A52',
                    fontSize: '14px',
                    fontWeight: 500,
                    marginBottom: '12px',
                    textAlign: 'left'
                  }}
                >
                  Year of Travel *
                </label>
                <select
                  value={yearOfTravel}
                  onChange={(e) => {
                    setYearOfTravel(e.target.value);
                    if (errors.yearOfTravel) setErrors({ ...errors, yearOfTravel: '' });
                  }}
                  className={`w-full bg-white rounded-xl border-none text-[#6B7280] focus:outline-none focus:ring-2 ${
                    errors.yearOfTravel ? 'ring-2 ring-red-500' : 'focus:ring-[#4A5568]'
                  }`}
                  style={{
                    width: '100%',
                    padding: '12px 56px 12px 16px',
                    paddingRight: '56px',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    border: errors.yearOfTravel ? '2px solid #EF4444' : 'none',
                    color: '#6B7280',
                    fontSize: '16px',
                    outline: 'none',
                    cursor: 'pointer',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 20px center',
                    backgroundSize: '16px'
                  }}
                  disabled={isLoading}
                >
                  <option value="">Select year</option>
                  {[...Array(11)].map((_, i) => {
                    const year = getCurrentYear() + i;
                    return (
                      <option key={year} value={year}>{year}</option>
                    );
                  })}
                </select>
                {errors.yearOfTravel && (
                  <p className="text-red-500 text-sm mt-2" style={{ color: '#EF4444', fontSize: '14px', marginTop: '8px' }}>
                    {errors.yearOfTravel}
                  </p>
                )}
              </div>

              {/* Lead Status - RADIO BUTTONS like main app */}
              <div style={{ marginBottom: '24px' }}>
                <label
                  className="block text-[#3A4A52] text-sm font-medium mb-3"
                  style={{
                    display: 'block',
                    color: '#3A4A52',
                    fontSize: '14px',
                    fontWeight: 500,
                    marginBottom: '12px',
                    textAlign: 'left'
                  }}
                >
                  Where are you in your planning? *
                </label>
                <div className="space-y-3">
                  <label
                    className="flex items-center cursor-pointer group"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      marginBottom: '12px'
                    }}
                  >
                    <input
                      type="radio"
                      name="status"
                      value="Researching"
                      checked={status === 'Researching'}
                      onChange={(e) => {
                        setStatus(e.target.value as 'Researching');
                        if (errors.status) setErrors({ ...errors, status: '' });
                      }}
                      className="w-5 h-5 border-2 border-[#9CA3AF] text-[#22475d] focus:ring-0 focus:outline-none cursor-pointer accent-[#22475d]"
                      style={{
                        width: '20px',
                        height: '20px',
                        marginRight: '12px',
                        cursor: 'pointer',
                        accentColor: '#22475d'
                      }}
                      disabled={isLoading}
                      required
                    />
                    <span
                      className="text-[#6B7280] group-hover:text-[#3A4A52] transition-colors"
                      style={{
                        color: '#6B7280',
                        fontSize: '16px',
                        transition: 'color 0.2s'
                      }}
                    >
                      Still Researching
                    </span>
                  </label>

                  <label
                    className="flex items-center cursor-pointer group"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      marginBottom: '12px'
                    }}
                  >
                    <input
                      type="radio"
                      name="status"
                      value="Ready to Book"
                      checked={status === 'Ready to Book'}
                      onChange={(e) => {
                        setStatus(e.target.value as 'Ready to Book');
                        if (errors.status) setErrors({ ...errors, status: '' });
                      }}
                      className="w-5 h-5 border-2 border-[#9CA3AF] text-[#22475d] focus:ring-0 focus:outline-none cursor-pointer accent-[#22475d]"
                      style={{
                        width: '20px',
                        height: '20px',
                        marginRight: '12px',
                        cursor: 'pointer',
                        accentColor: '#22475d'
                      }}
                      disabled={isLoading}
                      required
                    />
                    <span
                      className="text-[#6B7280] group-hover:text-[#3A4A52] transition-colors"
                      style={{
                        color: '#6B7280',
                        fontSize: '16px',
                        transition: 'color 0.2s'
                      }}
                    >
                      Ready to Book
                    </span>
                  </label>
                </div>
                {errors.status && (
                  <p className="text-red-500 text-sm mt-2" style={{ color: '#EF4444', fontSize: '14px', marginTop: '8px' }}>
                    {errors.status}
                  </p>
                )}
              </div>
            </>
          )}

            {/* Step 2: Trip Details (Extended Mode Only) */}
            {currentStep === 2 && (
              <>
                {/* Destination Single-Select */}
                <div style={{ marginBottom: '16px' }}>
                  <label
                    className="block text-[#3A4A52] text-sm font-medium mb-3"
                    style={{
                      display: 'block',
                      color: '#3A4A52',
                      fontSize: '14px',
                      fontWeight: 500,
                      marginBottom: '12px',
                      textAlign: 'left'
                    }}
                  >
                    Where would you like to go? *
                  </label>
                  <select
                    value={destination}
                    onChange={(e) => {
                      setDestination(e.target.value);
                      if (errors.destination) setErrors({ ...errors, destination: '' });
                    }}
                    className={`w-full bg-white rounded-xl border-none text-[#6B7280] focus:outline-none focus:ring-2 ${
                      errors.destination ? 'ring-2 ring-red-500' : 'focus:ring-[#4A5568]'
                    }`}
                    style={{
                      width: '100%',
                      padding: '12px 56px 12px 16px',
                      paddingRight: '56px',
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      border: errors.destination ? '2px solid #EF4444' : 'none',
                      color: '#6B7280',
                      fontSize: '16px',
                      outline: 'none',
                      cursor: 'pointer',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 20px center',
                      backgroundSize: '16px'
                    }}
                  >
                    <option value="">Select destination</option>
                    {destinationOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  {errors.destination && (
                    <p className="text-red-500 text-sm mt-2" style={{ color: '#EF4444', fontSize: '14px', marginTop: '8px' }}>
                      {errors.destination}
                    </p>
                  )}
                </div>

                {/* Travel Companions */}
                <div style={{ marginBottom: '16px' }}>
                  <label
                    className="block text-[#3A4A52] text-sm font-medium mb-3"
                    style={{
                      display: 'block',
                      color: '#3A4A52',
                      fontSize: '14px',
                      fontWeight: 500,
                      marginBottom: '12px',
                      textAlign: 'left'
                    }}
                  >
                    Who is travelling with you? *
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}>
                    {['Solo', 'Couple', 'Family', 'Friends'].map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => {
                          setTravelCompanions(option);
                          if (errors.travelCompanions) setErrors({ ...errors, travelCompanions: '' });
                        }}
                        style={{
                          padding: '0.75rem',
                          border: travelCompanions === option ? '2px solid #2C4A52' : '1px solid #D1D5DB',
                          backgroundColor: travelCompanions === option ? '#F0F4F5' : 'white',
                          borderRadius: '0.5rem',
                          cursor: 'pointer',
                          fontWeight: travelCompanions === option ? '600' : '400',
                          color: '#2C4A52'
                        }}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                  {errors.travelCompanions && (
                    <p style={{ color: '#EF4444', fontSize: '14px', marginTop: '8px' }}>
                      {errors.travelCompanions}
                    </p>
                  )}
                </div>

                {/* Budget */}
                <div style={{ marginBottom: '16px' }}>
                  <label
                    className="block text-[#3A4A52] text-sm font-medium mb-3"
                    style={{
                      display: 'block',
                      color: '#3A4A52',
                      fontSize: '14px',
                      fontWeight: 500,
                      marginBottom: '12px',
                      textAlign: 'left'
                    }}
                  >
                    Budget per person per night *
                  </label>
                  <select
                    value={budget}
                    onChange={(e) => {
                      setBudget(e.target.value);
                      if (errors.budget) setErrors({ ...errors, budget: '' });
                    }}
                    className={`w-full bg-white rounded-xl border-none text-[#6B7280] focus:outline-none focus:ring-2 ${
                      errors.budget ? 'ring-2 ring-red-500' : 'focus:ring-[#4A5568]'
                    }`}
                    style={{
                      width: '100%',
                      padding: '12px 56px 12px 16px',
                      paddingRight: '56px',
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      border: errors.budget ? '2px solid #EF4444' : 'none',
                      color: '#6B7280',
                      fontSize: '16px',
                      outline: 'none',
                      cursor: 'pointer',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 20px center',
                      backgroundSize: '16px'
                    }}
                  >
                    <option value="">Select budget range</option>
                    {budgetOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors.budget && (
                    <p className="text-red-500 text-sm mt-2" style={{ color: '#EF4444', fontSize: '14px', marginTop: '8px' }}>
                      {errors.budget}
                    </p>
                  )}
                </div>

                {/* Safari Nights */}
                <div style={{ marginBottom: '16px' }}>
                  <label
                    className="block text-[#3A4A52] text-sm font-medium mb-3"
                    style={{
                      display: 'block',
                      color: '#3A4A52',
                      fontSize: '14px',
                      fontWeight: 500,
                      marginBottom: '12px',
                      textAlign: 'left'
                    }}
                  >
                    How many nights of safari? *
                  </label>
                  <select
                    value={safariNights}
                    onChange={(e) => {
                      setSafariNights(e.target.value);
                      if (errors.safariNights) setErrors({ ...errors, safariNights: '' });
                    }}
                    className={`w-full bg-white rounded-xl border-none text-[#6B7280] focus:outline-none focus:ring-2 ${
                      errors.safariNights ? 'ring-2 ring-red-500' : 'focus:ring-[#4A5568]'
                    }`}
                    style={{
                      width: '100%',
                      padding: '12px 56px 12px 16px',
                      paddingRight: '56px',
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      border: errors.safariNights ? '2px solid #EF4444' : 'none',
                      color: '#6B7280',
                      fontSize: '16px',
                      outline: 'none',
                      cursor: 'pointer',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 20px center',
                      backgroundSize: '16px'
                    }}
                  >
                    <option value="">Select duration</option>
                    {nightsOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  {errors.safariNights && (
                    <p className="text-red-500 text-sm mt-2" style={{ color: '#EF4444', fontSize: '14px', marginTop: '8px' }}>
                      {errors.safariNights}
                    </p>
                  )}
                </div>
              </>
            )}


          {/* Action Buttons */}
          <div
            className="widget-action-buttons pt-4"
            style={{
              paddingTop: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}
          >
            {currentStep > 1 && (
              <button
                type="button"
                onClick={handlePrevStep}
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  border: '2px solid #22475d',
                  backgroundColor: 'white',
                  color: '#22475d',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  opacity: isLoading ? 0.5 : 1,
                  transition: 'all 0.2s'
                }}
              >
                Previous
              </button>
            )}

            {currentStep === totalSteps ? (
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#22475d] text-white py-3 px-6 rounded-xl font-semibold hover:bg-[#2d5a76] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  width: '100%',
                  backgroundColor: '#22475d',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: 600,
                  border: 'none',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  opacity: isLoading ? 0.5 : 1,
                  transition: 'background-color 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {isLoading ? (
                  <>
                    <svg
                      style={{
                        animation: 'spin 1s linear infinite',
                        marginRight: '8px',
                        width: '20px',
                        height: '20px'
                      }}
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        style={{ opacity: 0.25 }}
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        style={{ opacity: 0.75 }}
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Submitting...
                  </>
                ) : (
                  'Get Tour Operator Match'
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleNextStep}
                className="w-full bg-[#22475d] text-white py-3 px-6 rounded-xl font-semibold hover:bg-[#2d5a76] transition-colors"
                style={{
                  width: '100%',
                  backgroundColor: '#22475d',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: 600,
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
              >
                Continue
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
    </>
  );
}